/**
 * Scales and axis ticks.
 *
 * Ticks land on 1, 2, or 5 times a power of ten — the spacing that reads as
 * "someone chose this" rather than "a computer divided by seven".
 */

import type { PlotScale } from "../../shared/plot-spec.ts";
import type { Point, Segments } from "./compile.ts";

export interface Scale {
  /** Data value → pixel. */
  readonly to: (value: number) => number;
  /** Pixel → data value, for hover readouts. */
  readonly invert: (pixel: number) => number;
  readonly domain: readonly [number, number];
  readonly range: readonly [number, number];
  readonly kind: PlotScale;
}

export function createScale(
  domain: readonly [number, number],
  range: readonly [number, number],
  kind: PlotScale,
): Scale {
  if (kind === "log") return createLogScale(domain, range);
  return createLinearScale(domain, range);
}

function createLinearScale(
  domain: readonly [number, number],
  range: readonly [number, number],
): Scale {
  const [d0, d1] = domain;
  const [r0, r1] = range;
  const span = d1 - d0 || 1;
  const pixels = r1 - r0;

  return {
    kind: "linear",
    domain,
    range,
    to: (value) => r0 + ((value - d0) / span) * pixels,
    invert: (pixel) => d0 + ((pixel - r0) / pixels) * span,
  };
}

/** Log scales clamp to a positive floor; a non-positive domain is user error. */
function createLogScale(
  domain: readonly [number, number],
  range: readonly [number, number],
): Scale {
  const floor = 1e-12;
  const d0 = Math.max(domain[0], floor);
  const d1 = Math.max(domain[1], d0 * 10);
  const [r0, r1] = range;

  const l0 = Math.log10(d0);
  const l1 = Math.log10(d1);
  const span = l1 - l0 || 1;
  const pixels = r1 - r0;

  return {
    kind: "log",
    domain: [d0, d1],
    range,
    to: (value) =>
      r0 + ((Math.log10(Math.max(value, floor)) - l0) / span) * pixels,
    invert: (pixel) => 10 ** (l0 + ((pixel - r0) / pixels) * span),
  };
}

export interface Tick {
  readonly value: number;
  readonly label: string;
}

export function ticks(scale: Scale, count = 6): readonly Tick[] {
  return scale.kind === "log" ? logTicks(scale) : linearTicks(scale, count);
}

function linearTicks(scale: Scale, count: number): readonly Tick[] {
  const [start, end] = scale.domain;
  const step = niceStep((end - start) / Math.max(count, 1));
  if (!Number.isFinite(step) || step <= 0) return [];

  const first = Math.ceil(start / step) * step;
  const output: Tick[] = [];

  for (let value = first; value <= end + step * 1e-6; value += step) {
    // Re-round each tick: repeated addition drifts into 0.30000000000000004.
    const rounded = Math.round(value / step) * step;
    output.push({ value: rounded, label: formatTick(rounded, step) });
    if (output.length > 40) break;
  }

  return output;
}

function logTicks(scale: Scale): readonly Tick[] {
  const [start, end] = scale.domain;
  const from = Math.floor(Math.log10(start));
  const to = Math.ceil(Math.log10(end));
  const output: Tick[] = [];

  const decades = to - from;
  // Once the span is wide, decade labels alone are enough.
  const mantissas = decades > 4 ? [1] : decades > 2 ? [1, 3] : [1, 2, 5];

  for (let exponent = from; exponent <= to; exponent++) {
    for (const mantissa of mantissas) {
      const value = mantissa * 10 ** exponent;
      if (value < start * 0.999 || value > end * 1.001) continue;
      output.push({ value, label: formatLogTick(value) });
    }
  }

  return output;
}

/** Round a raw step up to the nearest 1, 2, or 5 times a power of ten. */
export function niceStep(raw: number): number {
  if (!Number.isFinite(raw) || raw <= 0) return 1;
  const exponent = Math.floor(Math.log10(raw));
  const magnitude = 10 ** exponent;
  const normalized = raw / magnitude;

  const nice = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return nice * magnitude;
}

export function formatTick(value: number, step: number): string {
  if (Math.abs(value) < step * 1e-6) return "0";

  const magnitude = Math.abs(value);
  if (magnitude >= 1e5 || (magnitude < 1e-3 && magnitude > 0)) {
    return formatExponential(value);
  }

  return value.toFixed(decimalsFor(step));
}

/**
 * The fewest decimals that still write the step exactly.
 *
 * Deriving this from `log10(step)` is off by one for steps like 0.25, which
 * would render two adjacent ticks as the same number.
 */
function decimalsFor(step: number): number {
  if (!Number.isFinite(step) || step <= 0) return 0;

  for (let decimals = 0; decimals <= MAX_TICK_DECIMALS; decimals++) {
    const factor = 10 ** decimals;
    if (Math.abs(Math.round(step * factor) / factor - step) < step * 1e-9) {
      return decimals;
    }
  }
  return MAX_TICK_DECIMALS;
}

const MAX_TICK_DECIMALS = 6;

function formatLogTick(value: number): string {
  const exponent = Math.log10(value);
  if (Number.isInteger(exponent) && (exponent < -2 || exponent > 3)) {
    return `10${superscript(exponent)}`;
  }
  return value >= 1 ? String(value) : String(Number(value.toPrecision(2)));
}

function formatExponential(value: number): string {
  const [mantissa, exponent] = value.toExponential(1).split("e");
  const cleaned = mantissa!.replace(/\.0$/, "");
  return `${cleaned}×10${superscript(Number(exponent))}`;
}

const SUPERSCRIPTS = "⁰¹²³⁴⁵⁶⁷⁸⁹";

function superscript(n: number): string {
  const sign = n < 0 ? "⁻" : "";
  const digits = Math.abs(n)
    .toString()
    .split("")
    .map((d) => SUPERSCRIPTS[Number(d)] ?? d)
    .join("");
  return sign + digits;
}

/* -------------------------------------------------------------------------- */
/* Autoscaling                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Choose a y-range from sampled data.
 *
 * Percentile-clipped rather than min/max: one sample near a pole would
 * otherwise flatten the entire curve into the axis. We keep the bulk of the
 * data visible and let the outlier run off the top, which is what a person
 * drawing this by hand would do.
 */
export function autoRange(
  segments: Segments,
  fallback: readonly [number, number] = [-1, 1],
): readonly [number, number] {
  const values: number[] = [];
  for (const segment of segments) {
    for (const point of segment) {
      if (Number.isFinite(point.y)) values.push(point.y);
    }
  }

  if (values.length === 0) return fallback;

  values.sort((a, b) => a - b);
  const low = percentile(values, 0.01);
  const high = percentile(values, 0.99);

  let min = low;
  let max = high;

  // A constant function still needs a window to sit in.
  if (max - min < 1e-9) {
    const centre = (max + min) / 2;
    const pad = Math.max(Math.abs(centre) * 0.1, 1);
    min = centre - pad;
    max = centre + pad;
  } else {
    const pad = (max - min) * 0.08;
    min -= pad;
    max += pad;
  }

  // Keep y = 0 in frame when the curve nearly touches it — it's a reference line.
  if (min > 0 && min < (max - min) * 0.35) min = 0;
  if (max < 0 && -max < (max - min) * 0.35) max = 0;

  return [min, max];
}

function percentile(sorted: readonly number[], fraction: number): number {
  const index = (sorted.length - 1) * fraction;
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  const weight = index - lower;
  return sorted[lower]! * (1 - weight) + sorted[upper]! * weight;
}

export function extentOf(points: readonly Point[]): {
  readonly x: readonly [number, number];
  readonly y: readonly [number, number];
} | null {
  if (points.length === 0) return null;

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  for (const point of points) {
    if (point.x < minX) minX = point.x;
    if (point.x > maxX) maxX = point.x;
    if (point.y < minY) minY = point.y;
    if (point.y > maxY) maxY = point.y;
  }

  return { x: [minX, maxX], y: [minY, maxY] };
}
